import type {
  PartnerPortalAttribution,
  PartnerPortalCommission,
  PartnerPortalOpportunity,
  PartnerPortalPayout,
  PartnerPortalPreview,
  PartnerPortalSummary,
  PartnerStatus,
  RevenueAttribution,
  RevenueCommissionEntry,
  RevenueOpportunity,
  RevenuePartner,
  RevenuePayoutItem,
  RevenuePayoutRecord,
  SalesPartnerRelationship,
  Tax1099ReadinessRow,
  PartnerPortalUserPreview,
  RevenuePartnerUserLink,
} from "../../types/revenueOps";

export type PartnerPortalBoundaryInput = {
  partner: RevenuePartner;
  opportunities?: RevenueOpportunity[];
  attributions?: RevenueAttribution[];
  commission_entries?: RevenueCommissionEntry[];
  payouts?: RevenuePayoutRecord[];
  payout_items?: RevenuePayoutItem[];
  tax_rows?: Tax1099ReadinessRow[];
  relationships?: SalesPartnerRelationship[];
  allow_estimated_values?: boolean;
};

export type PartnerPortalAccessInput = {
  authenticated: boolean;
  linked_partner_id?: string | null;
  requested_partner_id: string;
  partner_status: PartnerStatus;
};

export function canAccessPartnerPortalData(input: PartnerPortalAccessInput): boolean {
  return input.authenticated && input.linked_partner_id === input.requested_partner_id && input.partner_status === "active";
}

function isWonOpportunity(opportunity: RevenueOpportunity): boolean {
  return ["won", "closed_won"].includes(opportunity.stage);
}

export function isPartnerVisibleOpportunity(opportunity: RevenueOpportunity): boolean {
  return !["closed_lost", "lost", "rejected_duplicate", "duplicate", "rejected"].includes(opportunity.stage);
}

function isActiveOpportunity(opportunity: RevenueOpportunity): boolean {
  return isPartnerVisibleOpportunity(opportunity) && !isWonOpportunity(opportunity);
}

function isPreparedPayout(status: RevenuePayoutRecord["status"]): boolean {
  return ["draft", "pending_review", "approved", "partially_paid"].includes(status);
}

function unique(values: Array<string | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))];
}

export function isAdvisoryOverrideCommission(entry: RevenueCommissionEntry): boolean {
  const metadata = entry.metadata || {};
  const indicators = [
    entry.entry_type,
    metadata.entry_type,
    metadata.commission_type,
    metadata.relationship_type,
  ].filter(Boolean).map((value) => String(value).toLowerCase());
  return indicators.some((value) => value.includes("advisory") || value.includes("override") || value === "advisor");
}

export function buildPartnerPortalPreview(input: PartnerPortalBoundaryInput): PartnerPortalPreview {
  const { partner } = input;
  const opportunities = (input.opportunities || []).filter((item) => item.partner_id === partner.id && isPartnerVisibleOpportunity(item));
  const attributions = (input.attributions || []).filter((item) => item.partner_id === partner.id);
  const commissions = (input.commission_entries || []).filter((item) => item.partner_id === partner.id);
  const payoutById = new Map((input.payouts || []).map((item) => [item.id, item]));
  const payoutItems = (input.payout_items || []).filter((item) => item.partner_id === partner.id);
  const commissionById = new Map(commissions.map((item) => [item.id, item]));
  const taxRow = (input.tax_rows || []).find((item) => item.partner_id === partner.id);
  const allowed = canAccessPartnerPortalData({ authenticated: true, linked_partner_id: partner.id, requested_partner_id: partner.id, partner_status: partner.status });
  const payoutRows: PartnerPortalPayout[] = payoutItems.map((item) => {
    const batch = payoutById.get(item.payout_batch_id);
    return { batch_id: item.payout_batch_id, item_id: item.id, amount: item.amount, status: item.status === "paid" ? "paid" : batch?.status || item.status, paid_at: batch?.paid_date, payment_method: batch?.payment_method, payout_category: commissionById.get(item.commission_entry_id) && isAdvisoryOverrideCommission(commissionById.get(item.commission_entry_id) as RevenueCommissionEntry) ? "advisory_override" : "direct" };
  });
  const pendingCommissionTotal = commissions.filter((item) => ["pending", "pending_review"].includes(item.status) || item.review_status === "pending_review").reduce((sum, item) => sum + item.commission_amount, 0);
  const approvedCommissionTotal = commissions.filter((item) => ["approved", "payable", "paid"].includes(item.status)).reduce((sum, item) => sum + item.commission_amount, 0);
  const summary: PartnerPortalSummary = {
    partner_id: partner.id,
    partner_name: partner.display_name,
    partner_type: partner.partner_type,
    status: partner.status,
    opportunity_count: opportunities.length,
    active_opportunity_count: opportunities.filter(isActiveOpportunity).length,
    attributed_account_count: unique(attributions.map((item) => item.account_id || item.customer_name)).length,
    pending_commission_total: pendingCommissionTotal,
    approved_commission_total: approvedCommissionTotal,
    prepared_payout_total: payoutRows.filter((item) => item.status !== "paid" && item.status !== "canceled" && isPreparedPayout(item.status as RevenuePayoutRecord["status"])).reduce((sum, item) => sum + item.amount, 0),
    paid_payout_total: payoutRows.filter((item) => item.status === "paid").reduce((sum, item) => sum + item.amount, 0),
    tax_readiness_status: taxRow?.readiness_status,
    agreement_version: partner.agreement_version,
    payout_terms: partner.payout_terms,
  };
  const opportunityRows: PartnerPortalOpportunity[] = opportunities.map((item) => ({ id: item.id, account_label: item.account_name, stage: item.stage, estimated_value: input.allow_estimated_values ? item.estimated_mrr : undefined, created_at: item.created_at, updated_at: item.updated_at, partner_visible_notes: item.partner_visible_note }));
  const attributionRows: PartnerPortalAttribution[] = attributions.map((item) => ({ id: item.id, account_id: item.account_id, account_label: item.customer_name, status: item.status, effective_start: item.effective_start, effective_end: item.effective_end }));
  const commissionRows: PartnerPortalCommission[] = commissions.map((item) => ({ id: item.id, account_label: item.customer_account, eligible_amount: item.revenue_amount, commission_rate: item.commission_rate, commission_amount: item.commission_amount, status: item.status, review_status: item.review_status, reviewed_at: item.approved_at, commission_category: isAdvisoryOverrideCommission(item) ? "advisory_override" : "direct" }));
  return { access_allowed: allowed, access_reason: allowed ? undefined : "Partner data requires an authenticated user linked to an active sales partner.", summary, opportunities: opportunityRows, attributions: attributionRows, commissions: commissionRows, payouts: payoutRows };
}

export function buildPartnerUserVisibilityPreview(input: { user_id: string; links: RevenuePartnerUserLink[]; partners: RevenuePartner[]; shared: Omit<PartnerPortalBoundaryInput, "partner"> }): PartnerPortalUserPreview {
  const linkedPartnerIds = [...new Set(input.links.filter((link) => link.user_id === input.user_id && link.is_active).map((link) => link.partner_id))];
  const partnerPreviews = linkedPartnerIds.map((partnerId) => {
    const partner = input.partners.find((item) => item.id === partnerId);
    return partner ? buildPartnerPortalPreview({ ...input.shared, partner }) : null;
  }).filter((preview): preview is PartnerPortalPreview => Boolean(preview && preview.access_allowed));
  return {
    user_id: input.user_id,
    linked_partner_ids: partnerPreviews.map((preview) => preview.summary.partner_id),
    partner_previews: partnerPreviews,
    access_allowed: partnerPreviews.length > 0,
    access_reason: partnerPreviews.length ? undefined : "No active sales_partner_users link grants partner-safe preview data.",
  };
}

export function getPartnerPortalSafeFields() {
  return ["partner_profile_summary", "own_opportunities", "own_attributions", "own_commissions", "own_payouts", "own_tax_readiness", "agreement_and_payment_terms", "partner_visible_notes"] as const;
}

export function getPartnerPortalRedactedFields() {
  return ["stripe_webhook_events", "raw_stripe_payload", "internal_audit_log", "internal_notes", "other_partner_data", "total_toren_revenue", "admin_controls", "product_pricing_internals", "payment_reference"] as const;
}
